import { onBeforeUnmount } from "vue";

/**
 * Long-press detection for entering selection mode (the Drive/Photos gesture
 * that replaced BarkUp's always-visible checkboxes). Pointer-events based so
 * it works for both touch (LIFF webview) and mouse (desktop dev).
 *
 * Usage: spread the three handlers onto the row, and inside the row's click
 * handler call `consumeLongPress()` first — a long-press's release also fires
 * a click, and without this check every long-press would immediately also
 * "open" the item it just selected.
 */
export function useLongPress(onLongPress: () => void, { ms = 450, movePx = 10 } = {}) {
  let timer: number | null = null;
  let startX = 0;
  let startY = 0;
  let fired = false;

  const cancel = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  function onPointerdown(e: PointerEvent): void {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    fired = false;
    startX = e.clientX;
    startY = e.clientY;
    cancel();
    timer = window.setTimeout(() => {
      fired = true;
      onLongPress();
    }, ms);
  }

  function onPointermove(e: PointerEvent): void {
    // A drag (scroll attempt) is not a long-press.
    if (Math.abs(e.clientX - startX) > movePx || Math.abs(e.clientY - startY) > movePx) cancel();
  }

  function onPointerup(): void {
    cancel();
  }

  /** True exactly once after a long-press fired — callers use it to swallow the trailing click. */
  function consumeLongPress(): boolean {
    const was = fired;
    fired = false;
    return was;
  }

  onBeforeUnmount(cancel);

  return { onPointerdown, onPointermove, onPointerup, consumeLongPress };
}
